import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ArrowLeft, Settings as SettingsIcon, ShieldCheck } from "lucide-react";
import { useChat } from "../../context/ChatContext";
import { useAuth } from "../../context/AuthContext";
import { getEventById } from "../../services/eventService";
import { getChatStatus, deriveEventPermissions } from "../../utils/eventPermissions";
import MessageBubble from "../../components/chat/MessageBubble";
import MessageComposer from "../../components/chat/MessageComposer";
import ParticipantsStrip from "../../components/chat/ParticipantsStrip";
import MessageInfoModal from "../../components/chat/MessageInfoModal";
import ChatSettingsModal from "../../components/chat/ChatSettingsModal";
import PendingApprovalModal from "../../components/chat/PendingApprovalModal";
import ConfirmModal from "../../components/common/ConfirmModal";
import Loader from "../../components/common/Loader";
import EmptyState from "../../components/common/EmptyState";
import { color, radius } from "../../styles/theme";

/**
 * One event's chat thread — web port of mobile's `Screens/chat`, scoped to
 * text messages for Phase 1 (see the approved plan: media messages, the
 * Inform/bind-to-gallery buttons, and private 1:1 chat are deferred).
 * Chat-window open/closed logic reuses `getChatStatus`, already ported for
 * the read-only status line on the Event Details tab.
 */
const ChatThreadPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("chat");
  const chat = useChat();
  const { user, subAccountContext } = useAuth();

  const [event, setEvent] = useState(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [editingMessage, setEditingMessage] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showChatSettings, setShowChatSettings] = useState(false);
  const [showPendingApproval, setShowPendingApproval] = useState(false);

  const listRef = useRef(null);

  const fetchEvent = () =>
    getEventById(eventId)
      .then((res) => setEvent(res.data?.Result || res.data))
      .catch(() => toast.error(t("thread.loadError")));

  useEffect(() => {
    setEventLoading(true);
    fetchEvent().finally(() => setEventLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, t]);

  useEffect(() => {
    chat?.openRoom(eventId);
    return () => chat?.closeRoom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const messages = chat?.getRoomMessages(eventId) || [];
  const isLoading = chat?.isRoomLoading(eventId);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  const chatStatus = useMemo(() => getChatStatus(event), [event]);
  // Distinct from chatStatus.isChatEnabled above (the chat *time window*) —
  // this is Event.IsChatEnabled, the One-Way/Two-Way communication mode.
  const isTwoWayChat = event?.IsChatEnabled !== false;
  const permissions = useMemo(() => deriveEventPermissions(event, user, subAccountContext), [event, user, subAccountContext]);

  if (!chat) return null;

  const handleSend = async (text) => {
    try {
      await chat.sendMessage(eventId, text);
    } catch {
      toast.error(t("thread.sendError"));
    }
  };

  const handleSubmitEdit = async (text) => {
    try {
      await chat.editMessage(editingMessage, text);
      setEditingMessage(null);
    } catch {
      toast.error(t("thread.editError"));
    }
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await chat.removeMessage(deleteTarget);
      setDeleteTarget(null);
    } catch {
      toast.error(t("thread.deleteError"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button type="button" className="btn-ghost" style={s.backBtn} onClick={() => navigate("/chat")} aria-label={t("thread.back")}>
          <ArrowLeft size={17} strokeWidth={2.25} />
        </button>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={s.title}>{eventLoading ? "…" : event?.Name || event?.EventName}</div>
          {chat.participants.length > 0 && (
            <div style={s.subtitle}>{t("thread.participantsCount", { count: chat.participants.length })}</div>
          )}
        </div>
        {permissions.isAdmin && permissions.isMediaApprovalEligible && (
          <button
            type="button"
            className="btn-ghost"
            style={{ ...s.backBtn, position: "relative" }}
            onClick={() => setShowPendingApproval(true)}
            aria-label={t("pendingApproval.title")}
            title={t("pendingApproval.title")}
          >
            <ShieldCheck size={17} strokeWidth={2.25} />
            {chat.pendingApprovalCount > 0 && (
              <span style={s.pendingBadge}>{chat.pendingApprovalCount > 99 ? "99+" : chat.pendingApprovalCount}</span>
            )}
          </button>
        )}
        {permissions.isAdmin && (
          <button type="button" className="btn-ghost" style={s.backBtn} onClick={() => setShowChatSettings(true)} aria-label={t("settings.title")} title={t("settings.title")}>
            <SettingsIcon size={17} strokeWidth={2.25} />
          </button>
        )}
      </div>

      <ParticipantsStrip participants={chat.participants} presenceMap={chat.presenceMap} />

      <div ref={listRef} style={s.messageList}>
        {isLoading ? (
          <Loader />
        ) : messages.length === 0 ? (
          <EmptyState text={t("thread.empty")} />
        ) : (
          messages.map((m, i) => {
            const prev = messages[i - 1];
            const isOwn = chat.currentUserId != null && String(m.senderId) === String(chat.currentUserId);
            const showSenderName = !isOwn && (!prev || String(prev.senderId) !== String(m.senderId));
            return (
              <MessageBubble
                key={m.id}
                message={m}
                isOwn={isOwn}
                showSenderName={showSenderName}
                onEdit={setEditingMessage}
                onDelete={setDeleteTarget}
                onShowInfo={setInfoMessage}
                canModerate={permissions.isAdmin && permissions.isMediaApprovalEligible}
                onApprove={(msg, trustFuture) => chat.approveMessage(msg.moderationId, msg.id, trustFuture)}
                onReject={(msg, trustFuture) => chat.rejectMessage(msg.moderationId, msg.id, null, trustFuture)}
              />
            );
          })
        )}
      </div>

      <MessageComposer
        onSend={handleSend}
        editingMessage={editingMessage}
        onSubmitEdit={handleSubmitEdit}
        onCancelEdit={() => setEditingMessage(null)}
        disabled={!eventLoading && (!chatStatus.isChatEnabled || (!isTwoWayChat && !permissions.isAdmin))}
        disabledReason={
          !chatStatus.isChatEnabled
            ? chatStatus.chatStatus || t("thread.disabledEnded")
            : t("thread.oneWayDisabled")
        }
      />

      {infoMessage && (
        <MessageInfoModal message={infoMessage} participants={chat.participants} onClose={() => setInfoMessage(null)} />
      )}

      {showChatSettings && (
        <ChatSettingsModal
          eventId={eventId}
          event={event}
          mediaApprovalEligible={permissions.isMediaApprovalEligible}
          isARoomEvent={permissions.isARoomEvent}
          onClose={() => setShowChatSettings(false)}
          onUpdated={() => { setShowChatSettings(false); fetchEvent(); }}
        />
      )}

      {showPendingApproval && (
        <PendingApprovalModal
          eventId={eventId}
          onClose={() => setShowPendingApproval(false)}
          onReviewed={() => chat.refreshPendingApprovalCount(eventId)}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={t("thread.deleteConfirmTitle")}
          message={t("thread.deleteConfirmMessage")}
          danger
          loading={deleting}
          onConfirm={handleConfirmDelete}
          onCancel={() => !deleting && setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

const s = {
  page: { display: "flex", flexDirection: "column", height: "calc(100vh - 106px)", maxWidth: 760, background: color.surface, borderRadius: radius.lg, border: `1px solid ${color.border}`, overflow: "hidden" },
  header: { display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: `1px solid ${color.border}`, flexShrink: 0 },
  backBtn: { width: 32, height: 32, padding: 0, borderRadius: radius.sm, flexShrink: 0 },
  pendingBadge: {
    position: "absolute", top: -3, right: -3, minWidth: 15, height: 15, padding: "0 3px",
    borderRadius: 999, background: color.danger500, color: "#fff", fontSize: 9.5, fontWeight: 700,
    display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
  },
  title: { fontWeight: 700, fontSize: 15, color: color.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  subtitle: { fontSize: 11.5, color: color.textMuted, marginTop: 1 },
  messageList: { flex: 1, overflowY: "auto", padding: "16px" },
};

export default ChatThreadPage;
